import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Clock, Send, CheckCircle2, Loader2 } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMAGES, CONTACT } from "@/content/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "VAPT (Penetration Testing)",
  "Managed Security Services (SOCaaS)",
  "Enterprise Landing Zone",
  "Enterprise SIEM",
  "Enterprise XDR",
  "Cloud Security",
  "Open Source SOC Model",
  "Not sure yet",
];

const INDUSTRY_OPTIONS = [
  "Banking & Fintech",
  "Healthcare & Life Sciences",
  "SaaS & Technology",
  "Government & Defense",
  "E-Commerce & Retail",
  "Manufacturing & Energy",
  "Other",
];

const inputCls =
  "w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-[#A6B0C9] outline-none transition-[border-color,background-color] duration-200 focus:border-[#8E9AEA]/60 focus:bg-white/[0.07]";

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-[#A6B0C9]">
      {label}
    </span>
    {children}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: SERVICE_OPTIONS[0],
    industry: INDUSTRY_OPTIONS[0],
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("done");
      toast.success("Consultation request received. We'll respond within one business day.");
    } catch (err) {
      setStatus("idle");
      toast.error(
        err.response?.data?.error || "Something went wrong. Please try again."
      );
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        breadcrumb={["Home", "Contact"]}
        pill="Get a Consultation"
        titleLines={[
          [{ t: "Let's Talk About" }],
          [{ t: "Your ", }, { t: "Security", accent: true }],
        ]}
        description="Tell us about your environment, your compliance targets and your threat concerns. A Lanvaus security architect will respond within one business day."
        image={IMAGES.padlock}
        showCompliance={false}
        primaryCta={null}
        secondaryCta={null}
      />

      <section className="mx-auto max-w-7xl px-5 py-16 sm:py-24 sm:px-8 lg:px-16" data-testid="contact-section">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.4fr]">
          <Reveal>
            <span className="pill-badge">Contact Details</span>
            <h2 className="mt-6 text-[1.75rem] font-bold leading-[1.2] tracking-tight text-white sm:text-4xl sm:leading-tight">
              Direct line to our{" "}
              <span className="text-[#8E9AEA]">security architects</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-[#DDE3F2]">
              Whether you need an urgent penetration test before an audit, a managed
              SOC for 24/7 coverage, or guidance on the Open Source SOC Model — start
              here.
            </p>
            <div className="mt-10 flex flex-col gap-6">
              {[
                { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testId: "contact-email" },
                { icon: Phone, label: "Phone (US / International)", value: CONTACT.phones[0].value, href: CONTACT.phones[0].href, testId: "contact-phone" },
                { icon: Phone, label: "Phone (India)", value: CONTACT.phones[1].value, href: CONTACT.phones[1].href, testId: "contact-phone-india" },
                { icon: MapPin, label: "Location", value: CONTACT.location, testId: "contact-location" },
                { icon: Clock, label: "Response Time", value: "Within one business day", testId: "contact-response-time" },
              ].map((c) => (
                <div key={c.label} className="flex items-start gap-4" data-testid={c.testId}>
                  <span className="rounded-lg border border-white/10 bg-white/5 p-3">
                    <c.icon className="h-5 w-5 text-[#8E9AEA]" />
                  </span>
                  <span>
                    <span className="block text-[11px] font-bold uppercase tracking-[0.2em] text-[#A6B0C9]">
                      {c.label}
                    </span>
                    {c.href ? (
                      <a href={c.href} className="mt-1 block text-base font-medium text-white transition-colors duration-200 hover:text-[#8E9AEA]">
                        {c.value}
                      </a>
                    ) : (
                      <span className="mt-1 block text-base font-medium text-white">{c.value}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            {status === "done" ? (
              <div
                className="glass-card flex h-full flex-col items-center justify-center p-12 text-center"
                data-testid="contact-success"
              >
                <span className="rounded-full border border-[#8E9AEA]/40 bg-[#8E9AEA]/10 p-5">
                  <CheckCircle2 className="h-10 w-10 text-[#8E9AEA]" />
                </span>
                <h3 className="mt-7 text-2xl font-bold text-white">
                  Request received.
                </h3>
                <p className="mt-3 max-w-sm text-base leading-relaxed text-[#DDE3F2] sm:text-sm">
                  Thank you, {form.name.split(" ")[0]}. A Lanvaus security architect
                  will reach out to <span className="text-white">{form.email}</span>{" "}
                  within one business day.
                </p>
                <button
                  className="btn-secondary mt-8"
                  onClick={() => {
                    setStatus("idle");
                    setForm({ name: "", email: "", company: "", phone: "", service: SERVICE_OPTIONS[0], industry: INDUSTRY_OPTIONS[0], message: "" });
                  }}
                  data-testid="contact-send-another-btn"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                className="glass-card !transform-none p-6 sm:p-10"
                data-testid="contact-form"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <Field label="Full Name *" testId="field-name">
                    <input
                      required
                      className={inputCls}
                      placeholder="Jane Sharma"
                      value={form.name}
                      onChange={set("name")}
                      data-testid="contact-name-input"
                    />
                  </Field>
                  <Field label="Work Email *" testId="field-email">
                    <input
                      required
                      type="email"
                      className={inputCls}
                      placeholder="jane@company.com"
                      value={form.email}
                      onChange={set("email")}
                      data-testid="contact-email-input"
                    />
                  </Field>
                  <Field label="Company" testId="field-company">
                    <input
                      className={inputCls}
                      placeholder="Company Pvt. Ltd."
                      value={form.company}
                      onChange={set("company")}
                      data-testid="contact-company-input"
                    />
                  </Field>
                  <Field label="Phone" testId="field-phone">
                    <input
                      className={inputCls}
                      placeholder="+91 …"
                      value={form.phone}
                      onChange={set("phone")}
                      data-testid="contact-phone-input"
                    />
                  </Field>
                  <Field label="Service of Interest" testId="field-service">
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.service}
                      onChange={set("service")}
                      data-testid="contact-service-select"
                    >
                      {SERVICE_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-[#141B30]">
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Industry" testId="field-industry">
                    <select
                      className={`${inputCls} appearance-none`}
                      value={form.industry}
                      onChange={set("industry")}
                      data-testid="contact-industry-select"
                    >
                      {INDUSTRY_OPTIONS.map((o) => (
                        <option key={o} value={o} className="bg-[#141B30]">
                          {o}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="How can we help? *" testId="field-message">
                      <textarea
                        required
                        rows={5}
                        className={`${inputCls} resize-none`}
                        placeholder="Tell us about your environment, upcoming audits, or security concerns…"
                        value={form.message}
                        onChange={set("message")}
                        data-testid="contact-message-input"
                      />
                    </Field>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="btn-primary mt-8 w-full disabled:opacity-60 sm:w-auto"
                  data-testid="contact-submit-btn"
                >
                  {status === "sending" ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      Request Consultation
                      <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
                <p className="mt-4 text-xs text-[#A6B0C9]">
                  Your details are stored securely and used only to respond to your
                  enquiry.
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
